import { Component, Input, OnDestroy } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import * as encodingDetect from 'encoding-japanese';
import * as iconvLite from 'iconv-lite';
import * as papaparse from 'papaparse';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { CsvField, CsvColumn, CsvImportDialogService, ImportedFile } from './csv-import-dialog.service';

import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';

@Component({
    selector: 'agpr-csv-import-dialog',
    templateUrl: './csv-import-dialog.component.html',
    styleUrls: ['./csv-import-dialog.component.scss'],
    standalone: false
})
export class CsvImportDialogComponent<T, Y> extends AbstractDialogComponent implements OnDestroy {

    @Input() public id: string;
    @Input() heading: string;
    @Input() description: string;

    @Input() confirmAction: () => Observable<any>;

    loading: boolean = false;
    subscriptions: Subscription[] = [];

    rawFile: ImportedFile<T, Y>;
    csvFields: CsvField<T>[];

    csvColumns: CsvColumn[];
    csvColumnDropdownOptions: DropdownOptionModel[] = [];
    selectedOptions: DropdownOptionModel[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    constructor(
        public coreDialogService: CoreDialogService,
        public csvImportDialogService: CsvImportDialogService<T, Y>,
        public messageService: MessageService
    ) {
        super(coreDialogService, 'CsvImport');
        this.subscriptions.push(
            this.csvImportDialogService.importedFiles$.subscribe(files => {
                this.rawFile = files[0];
                this.csvColumns = files[0].csvColumns;
                this.updateDropdownOptions();
                this.csvFields = files[0].csvFields;
            })
        );
    }

    fileSelected(event) {
        const files: FileList = event.target.files;
        const file: File = files[0];
        const reader = new FileReader();

        reader.readAsArrayBuffer(file);
        reader.onload = (loadEvent: any) => {
            const byteArray = new Uint8Array(loadEvent.target.result as ArrayBuffer);
            let fileEncoding = encodingDetect.detect(byteArray);

            // The detecting library isn't very good.  This
            // is about the best we can do with it.
            if (fileEncoding !== 'UTF8') {
                fileEncoding = 'ISO-8859-1';
            }

            const contentString = iconvLite.decode(Buffer.from(byteArray), fileEncoding);

            papaparse.parse<string[]>(contentString, {
                skipEmptyLines: true,
                header: false,
                complete: (results) => {
                    this.csvImportDialogService.processData(file.name, results.data);
                }
            });
        };
    }

    ok(): void {
        if (this.csvImportDialogService.getValidationErrors().length > 0) {
            this.csvImportDialogService.importData().subscribe({
                next: done => this.closeDialog(true),
                error: err => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        detail: 'Monitoring.csvImportDialog.importError'
                    });
                    this.closeDialog(true);
                }
            });
        } else {
            this.messageService.add({
                severity : messageSeverityEnum.error,
                detail: 'Monitoring.csvImportDialog.missingFields'
            });
        }
    }

    cancel(): void {
        this.closeDialog(false);
    }


    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onDropdownChanged(option: DropdownOptionModel, field: CsvField<T> ){
        field.targetColumn = option.value;
        this.csvImportDialogService.updateExampleValue(this.rawFile, field);
    }

    private updateDropdownOptions() {
        this.csvColumnDropdownOptions = [];
        if (this.csvColumns && this.csvColumns.length > 0) {
            this.csvColumns.forEach(column => {
                this.csvColumnDropdownOptions.push({
                    label: column.columnLabel,
                    value: column
                });
            });
        }

        this.selectedOptions = [];
        if (this.csvFields && this.csvFields.length > 0) {
            for (let i = 0; i < this.csvFields.length; i++) {
                this.selectedOptions[i] = this.csvColumnDropdownOptions.find(option => this.csvFields[i].targetColumn === option.value);
            }
        }
    }
}
